/* Material details drawer on the PDP — the winning V2 variant from ITT-6589,
 * productionised for the theme (ITT-6886).
 *
 * The whole drawer, related material products included, is rendered server-side by
 * snippets/material-details-drawer.liquid, so this file only drives open/close, focus
 * and event tracking. Nothing is built here and no request is made at runtime.
 */
(function () {
  'use strict';

  function publish(eventName, payload) {
    if (typeof window.luxoPublishEvent !== 'function') return;
    window.luxoPublishEvent(eventName, payload || {});
  }

  if (!customElements.get('material-drawer-panel')) {
    customElements.define(
      'material-drawer-panel',
      class MaterialDrawerPanel extends HTMLElement {
        connectedCallback() {
          if (this._wired) return;
          this._wired = true;

          this.sheet = this.querySelector('.material-drawer__sheet');
          this.closeBtn = this.querySelector('[data-drawer-close]');
          this.backdrop = this.querySelector('[data-drawer-backdrop]');
          this._boundEscape = this._handleEscape.bind(this);
          this._boundClickOutside = this._handleClickOutside.bind(this);

          var self = this;
          if (this.closeBtn) {
            this.closeBtn.addEventListener('click', function () {
              self.closePanel('close_button');
            });
          }
          if (this.backdrop) {
            this.backdrop.addEventListener('click', function () {
              self.closePanel('backdrop');
            });
          }

          // A fixed-position sheet is positioned against the nearest transformed
          // ancestor, and the theme's scroll animations leave transforms on section
          // wrappers. Moving the panel to <body> keeps it anchored to the viewport.
          if (this.parentElement !== document.body) document.body.appendChild(this);
        }

        get isOpen() {
          return this.hasAttribute('data-open');
        }

        openPanel() {
          if (this.isOpen) return;
          this.removeAttribute('hidden');
          this.setAttribute('aria-hidden', 'false');
          this.setAttribute('data-open', '');

          // Force a reflow so the browser registers the closed state
          // (display:none -> block, translateX(100%)) before is-visible flips it —
          // otherwise the slide-in transition is skipped and it snaps.
          void this.offsetWidth;
          var self = this;
          requestAnimationFrame(function () {
            self.classList.add('is-visible');
          });

          document.body.style.overflow = 'hidden';
          document.addEventListener('keydown', this._boundEscape);
          // Registered on the next tick so the click that opened the drawer does not
          // immediately close it again.
          setTimeout(function () {
            document.addEventListener('click', self._boundClickOutside, true);
          }, 0);

          if (this.sheet) this.sheet.focus();

          window.__materialDrawerOpened = true;
          publish('material_drawer_opened', {
            product_id: this.dataset.productId || '',
            material: this.dataset.material || '',
          });
        }

        closePanel(method) {
          if (!this.isOpen) return;
          this.classList.remove('is-visible');
          this.setAttribute('aria-hidden', 'true');
          this.removeAttribute('data-open');
          document.body.style.overflow = '';
          document.removeEventListener('keydown', this._boundEscape);
          document.removeEventListener('click', this._boundClickOutside, true);

          var self = this;
          clearTimeout(this._hideTimer);
          this._hideTimer = setTimeout(function () {
            self.setAttribute('hidden', '');
          }, 250);

          if (this.trigger) {
            this.trigger.setAttribute('aria-expanded', 'false');
            this.trigger.focus();
          }

          publish('material_drawer_closed', {
            product_id: this.dataset.productId || '',
            material: this.dataset.material || '',
            method: method || 'unknown',
          });
        }

        _handleClickOutside(event) {
          // Anything outside the sheet closes the drawer — covers the backdrop and any
          // click that lands on the page behind it.
          if (this.sheet && !this.sheet.contains(event.target)) this.closePanel('click_away');
        }

        _handleEscape(event) {
          if (event.key === 'Escape') this.closePanel('escape');
        }
      }
    );
  }

  if (!customElements.get('material-drawer-trigger')) {
    customElements.define(
      'material-drawer-trigger',
      class MaterialDrawerTrigger extends HTMLElement {
        connectedCallback() {
          var btn = this.querySelector('button');
          if (!btn || this._wired) return;
          this._wired = true;

          btn.addEventListener('click', function () {
            var panel = document.getElementById(btn.getAttribute('aria-controls'));
            if (!panel || typeof panel.openPanel !== 'function') return;
            panel.trigger = btn;
            btn.setAttribute('aria-expanded', 'true');
            panel.openPanel();
          });
        }
      }
    );
  }

  /* Swatch and add-to-cart tracking is page-level, so it is bound once. */
  if (!window.__materialDrawerPageTracking) {
    window.__materialDrawerPageTracking = true;

    document.addEventListener(
      'click',
      function (event) {
        var swatch = event.target.closest('.product-swatches [data-swatch]');
        if (swatch) {
          publish('material_swatch_interacted', {
            material: swatch.getAttribute('title') || '',
            source: 'pdp',
          });
          return;
        }

        var similar = event.target.closest('[data-similar-product]');
        if (similar) {
          publish('material_swatch_interacted', {
            material: similar.querySelector('.material-drawer__similar-title')
              ? similar.querySelector('.material-drawer__similar-title').textContent.trim()
              : '',
            source: 'drawer_similar',
          });
          return;
        }

        var addButton = event.target.closest('[name="add"]');
        if (!addButton) return;
        // Quick-add buttons on product cards are not the PDP add-to-cart.
        if (addButton.closest('.card-wrapper, .card-product, quick-add-modal')) return;

        var panel = document.querySelector('material-drawer-panel');
        publish('pdp_add_to_cart', {
          product_id: panel ? panel.dataset.productId || '' : '',
          material: panel ? panel.dataset.material || '' : '',
          material_drawer_opened: !!window.__materialDrawerOpened,
          material_drawer_open: !!(panel && panel.hasAttribute('data-open')),
        });
      },
      true
    );
  }
})();
